import gtmService from '@/shared/tracking/gtm.service';
import { GA4Item, GtmItemListId, GtmItemListName, GtmAddToCartProduct } from '@/shared/tracking/gtm.model';
import { BaseProductItem, BasketCheckoutModel, BasketDeliverable, BasketModel, CheckOutDeliverable, CheckoutDeliverableType, OrderReceiptBaseItemModel, OrderReceiptDeliverable, OrderReceiptDeliverableType, OrderReceiptModel, PersonalOfferBaseItemModel, PersonalOfferItemType, PersonalOfferModuleSofaItemModel, PersonalOfferRegularItemModel, PersonalOfferViewModel, WishList, WishListItem } from '@/b2c/shared/api/server-apis';
import { OutletProductDetailsModel, ProductAddOnModel, ProductDetailModel, ProductSpotModel, PurchaseOptionType } from '@/types/productServerApi';
import pageStore from '@/shared/content/page.store';
import basketStore from '@/shared/checkout/basket/basket.store';
interface AddToBasketProduct {
    id?: string,
    name: string,
    designerName?: string,
    price?: number | string,
    quantity: number,
    ean?: string,
    familyName?: string,
    itemListId: GtmItemListId,
    itemListName: GtmItemListName,
}

class BasketTrackingService {
    private getGtmAddToCartProduct({ id, name, designerName, price, quantity, ean, familyName, itemListId, itemListName }: AddToBasketProduct): GtmAddToCartProduct {
        return {
            id: id,
            name: name,
            brand: designerName,
            price: price,
            quantity,
            dimension8: ean,
            dimension14: familyName,
            // eslint-disable-next-line quote-props
            'item_list_id': itemListId,
            // eslint-disable-next-line quote-props
            'item_list_name': itemListName,
        };
    }

    public product(product: ProductDetailModel, options?: { quantity?: number, purchaseOption?: PurchaseOptionType }): void {
        const products: GA4Item = gtmService.getGA4ItemFromProductDetailModel(
            {
                product,
                isModule: false,
                quantity: options?.quantity ?? 1,
                itemListId: 'pdp',
                itemListName: 'PDP',
                shippingTier: this.getPurchaseOptionAsString(options?.purchaseOption),
            });

        gtmService.ga4AddToCart([products], { currency: product.price.salesPrice.currency, value: product.price.salesPrice.rawAmount, shipping_tier: this.getPurchaseOptionAsString(options?.purchaseOption) });

        // SUPPORT FOR OLD GTM
        const productsOld: GtmAddToCartProduct = this.getGtmAddToCartProduct(
            {
                id: product?.id,
                name: product?.fullName,
                designerName: product?.designer,
                price: product?.price.salesPrice.rawAmount,
                quantity: options?.quantity ?? 1,
                ean: product.specifications.ean,
                familyName: product?.familyName,
                itemListId: 'pdp',
                itemListName: 'PDP',
            });
        gtmService.addToCart([productsOld], product.price.salesPrice.currency);
    }

    public basketQuantity(basket: BasketModel, itemId: number): void {
        const items: GA4Item[] = [];
        let product: BaseProductItem | undefined;
        let index = 0;
        let deliverable: BasketDeliverable | undefined;
        basket.deliverables.forEach((d, outerIndex) => {
            const flattenedIndexOffset = basket.deliverables.slice(0, outerIndex).reduce((acc, curr) => acc + curr.items.length, 0);
            d.items.forEach((i, innerIndex) => {
                if (i.id === itemId) {
                    deliverable = d;
                    product = i as BaseProductItem;
                    index = flattenedIndexOffset + innerIndex;
                }
            });
        });

        if (product) {
            const productOrModules = this.getProductOrModules(product);
            productOrModules.forEach((product) => {
                items.push(
                    gtmService.getGA4Item({
                        product,
                        isModule: false,
                        promoCodes: basket.summary.appliedPromoCodes,
                        index,
                        shippingTier: this.getShippingMethodAsString(deliverable?.type),
                        itemListId: 'basket_quantity',
                        itemListName: 'Basket Quantity',
                    }),
                );
            });
        }

        gtmService.ga4AddToCart(
            items,
            {
                currency: basket.summary.salesPriceTotal?.currency,
                value: basket.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(basket.deliverables),
            },
        );
    }

    private getPurchaseOptionAsString(method?: PurchaseOptionType) {
        switch (method) {
        case PurchaseOptionType.ClickAndCollect:
            return 'click and collect';
        case PurchaseOptionType.Standard:
            return 'standard';
        case PurchaseOptionType.PickAndPack:
            return 'pick and pack';
        case PurchaseOptionType.ModuleConfiguration:
            return 'module configuration';
        default:
            return undefined;
        }
    }

    private getOrderReceiptDeliverableTypeAsString(method: OrderReceiptDeliverableType) {
        switch (method) {
        case OrderReceiptDeliverableType.ClickAndCollect:
            return 'click and collect';
        case OrderReceiptDeliverableType.CustomerDelivery:
            return 'standard';
        case OrderReceiptDeliverableType.PickAndPack:
            return 'pick and pack';
        default:
            return undefined;
        }
    }

    public basketRecommendedProducts(basket: BasketModel, product: ProductSpotModel, purchaseOption: PurchaseOptionType, index: number): void {
        const items: GA4Item[] = [];

        items.push(
            gtmService.getGA4ItemFromProductSpotModel({
                product,
                isModule: false,
                promoCodes: basket.summary.appliedPromoCodes,
                index,
                shippingTier: this.getPurchaseOptionAsString(purchaseOption),
                itemListId: 'basket_recommended',
                itemListName: 'Basket Recommended',
            }),
        );

        gtmService.ga4AddToCart(
            items,
            {
                currency: basket.summary.salesPriceTotal?.currency,
                value: basket.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(basket.deliverables),
            },
        );
    }

    public outlet(outletProduct: OutletProductDetailsModel, quantity: number = 1): void {
        const products: GA4Item = gtmService.getGA4ItemFromOutletProductDetailsModel(
            {
                product: outletProduct,
                quantity,
                itemListId: 'outlet_pdp',
                itemListName: 'Outlet PDP',
            });
        gtmService.ga4AddToCart([products], { currency: outletProduct.price.salesPrice.currency, value: outletProduct.price.salesPrice.rawAmount });

        // SUPPORT FOR OLD GTM
        const productsOld: GtmAddToCartProduct = this.getGtmAddToCartProduct(
            {
                id: outletProduct?.inventSerial,
                name: outletProduct?.fullName,
                designerName: outletProduct?.designer?.name,
                price: outletProduct?.price.salesPrice.rawAmount,
                quantity,
                ean: '',
                familyName: outletProduct?.familyName,
                itemListId: 'outlet_pdp',
                itemListName: 'Outlet PDP',
            });
        gtmService.addToCart([productsOld], outletProduct.price.salesPrice.currency);
    }

    public powerstep(sku: string, upsellItems: ProductSpotModel[]): void {
        const productSpot = upsellItems.find((item) => item.sku === sku);

        if (productSpot) {
            const product: GA4Item = gtmService.getGA4ItemFromProductSpotModel({
                product: productSpot,
                itemListId: 'pdp_powerstep',
                itemListName: 'PDP Powerstep',

            });
            gtmService.ga4AddToCart([product], { currency: productSpot.price.salesPrice.currency });

            // SUPPORT FOR OLD GTM
            const productOld: GtmAddToCartProduct = this.getGtmAddToCartProduct({
                name: productSpot.name,
                id: productSpot.sku,
                designerName: productSpot.designerName,
                price: productSpot.price.salesPrice.rawAmount,
                ean: productSpot.ean,
                familyName: productSpot.familyName,
                quantity: product.quantity ?? 1,
                itemListId: 'pdp_powerstep',
                itemListName: 'PDP Powerstep',

            });
            gtmService.addToCart([productOld], productSpot.price.salesPrice.currency);
        }
    }

    private getCustomerOffersToGA4Items(items: PersonalOfferBaseItemModel[], itemListId: GtmItemListId, itemListName: GtmItemListName) {
        const products: GA4Item[] = [];
        if (items) {
            items.forEach((item, index) => {
                if (item.type === PersonalOfferItemType.ModuleSofa) {
                    (item as PersonalOfferModuleSofaItemModel).modules.forEach((module) => {
                        products.push(gtmService.getGA4ItemFromPersonalOfferRegularItemModel(
                            { product: module, isModule: true, itemListId, itemListName, index },
                        ));
                    });
                } else {
                    products.push(gtmService.getGA4ItemFromPersonalOfferRegularItemModel(
                        { product: item as PersonalOfferRegularItemModel, itemListId, itemListName, isModule: false, index },
                    ));
                }
            });
        }

        return products;
    }

    // SUPPORT FOR OLD GTM
    private getPersonalOfferRegularItem(item: PersonalOfferRegularItemModel, itemListId: GtmItemListId, itemListName: GtmItemListName): GtmAddToCartProduct {
        return this.getGtmAddToCartProduct({
            id: item.sku,
            name: item.variant.name,
            designerName: item.variant.designerName,
            ean: item.variant.ean,
            familyName: item.variant.familyName,
            price: item.price.salesPriceTotal?.rawAmount,
            quantity: item.quantity,
            itemListId,
            itemListName,
        });
    }

    public customerOfferItem(offer: PersonalOfferViewModel, lineItemId: string): void {
        const itemListId = pageStore.isPublicB2CPageLayout ? 'public_offer_product' : 'mb_offer_product';
        const itemListName = pageStore.isPublicB2CPageLayout ? 'Public Offer - Product' : 'MyBolia Offer - Product';
        const product = offer.items.find((item) => item.id === lineItemId);
        if (product) {
            const products: GA4Item[] = this.getCustomerOffersToGA4Items([product], itemListId, itemListName);

            gtmService.ga4AddToCart(products, { currency: offer.items[0].price.salesPriceTotal?.currency, value: product.price.salesPriceTotal?.rawAmount });
        }

        // SUPPORT FOR OLD GTM
        const products: GtmAddToCartProduct[] = [];
        offer.items.forEach((item) => {
            if (item.id === lineItemId) {
                if (item.type === PersonalOfferItemType.ModuleSofa) {
                    (item as PersonalOfferModuleSofaItemModel).modules.forEach((module) => {
                        products.push(this.getPersonalOfferRegularItem(module, itemListId, itemListName));
                    });
                } else {
                    products.push(this.getPersonalOfferRegularItem(item as PersonalOfferRegularItemModel, itemListId, itemListName));
                }
            }
        });
        gtmService.addToCart(products, offer?.items[0].price.salesPriceTotal?.currency ?? '');
    }

    public customerOfferAll(offer: PersonalOfferViewModel): void {
        const itemListId = pageStore.isPublicB2CPageLayout ? 'public_offer_all' : 'mb_offer_all';
        const itemListName = pageStore.isPublicB2CPageLayout ? 'Public Offer - All' : 'MyBolia Offer - All';
        const products: GA4Item[] = this.getCustomerOffersToGA4Items(offer.items, itemListId, itemListName);

        try {
            gtmService.ga4AddToCart(products, { currency: offer.items[0].price.salesPriceTotal?.currency, value: offer.totalPrice.rawAmount });
        } catch (e) {
            console.log('Error parsing offer total price', e);
        }

        // SUPPORT FOR OLD GTM
        if (offer.items) {
            const productsOld: GtmAddToCartProduct[] = [];
            offer.items.forEach((item) => {
                if (item.type === PersonalOfferItemType.ModuleSofa) {
                    (item as PersonalOfferModuleSofaItemModel).modules.forEach((module) => {
                        productsOld.push(this.getPersonalOfferRegularItem(module, itemListId, itemListName));
                    });
                } else {
                    productsOld.push(this.getPersonalOfferRegularItem(item as PersonalOfferRegularItemModel, itemListId, itemListName));
                }
            });
            gtmService.addToCart(productsOld, offer?.items[0].price.salesPriceTotal?.currency ?? '');
        }
    }

    public wishlistItem(product: WishListItem, isPublic: boolean = false): void {
        const itemListId = isPublic ? 'public_wishlist_product' : 'mb_wishlist_product';
        const itemListName = isPublic ? 'Public Wishlist - Product' : 'MyBolia Wishlist - Product';
        const products: GA4Item = gtmService.getGA4ItemFromWishListItem(
            {
                product,
                itemListId,
                itemListName,
            });
        gtmService.ga4AddToCart([products], { currency: product.price.salesPrice.currency, value: product.price.salesPrice.rawAmount });

        // SUPPORT FOR OLD GTM
        const productsOld: GtmAddToCartProduct = this.getGtmAddToCartProduct(
            {
                id: product.variant.sku,
                name: product.variant.name,
                designerName: product?.variant.designerName,
                price: product?.price?.salesPrice.rawAmount,
                quantity: 1,
                ean: product.variant.ean,
                familyName: product?.variant.familyName,
                itemListId,
                itemListName,
            });
        gtmService.addToCart([productsOld], product.price.listPrice.currency);
    }

    public wishlistAll(list: WishList, isPublic: boolean = false): void {
        const itemListId = isPublic ? 'public_wishlist_all' : 'mb_wishlist_all';
        const itemListName = isPublic ? 'Public Wishlist - All' : 'MyBolia Wishlist - All';

        const products: GA4Item[] = list.wishListItems.map((product) => {
            return gtmService.getGA4ItemFromWishListItem(
                {
                    product,
                    itemListId,
                    itemListName,
                });
        });
        gtmService.ga4AddToCart(products, { currency: list.wishListItems[0].price.salesPrice.currency });

        // SUPPORT FOR OLD GTM
        const productsOld: GtmAddToCartProduct[] = list.wishListItems.map((product) => {
            return this.getGtmAddToCartProduct(
                {
                    id: product.variant.sku,
                    name: product.variant.name,
                    designerName: product?.variant.designerName,
                    price: product?.price?.salesPrice.rawAmount,
                    quantity: 1,
                    ean: product.variant.ean,
                    familyName: product?.variant.familyName,
                    itemListId,
                    itemListName,
                });
        });
        gtmService.addToCart(productsOld, list.wishListItems[0].price.listPrice.currency);
    }

    public addOns(addOns: ProductAddOnModel[], purchaseType?: PurchaseOptionType) {
        if (addOns.length === 0) {
            return;
        }
        const products: GA4Item[] = addOns.map((addOn) => {
            return gtmService.getGA4ItemFromProductAddOnModel(
                {
                    product: addOn,
                    isModule: false,
                    itemListId: 'pdp_add_ons',
                    itemListName: 'PDP Add-ons',
                    shippingTier: this.getPurchaseOptionAsString(purchaseType),
                });
        });
        gtmService.ga4AddToCart(products, { currency: addOns[0].price.salesPrice.currency, value: addOns.reduce((acc, curr) => acc + curr.price.salesPrice.rawAmount, 0) });

        // SUPPORT FOR OLD GTM
        const productsOld: GtmAddToCartProduct[] = addOns.map((addOn) => {
            return this.getGtmAddToCartProduct(
                {
                    id: addOn.sku,
                    name: addOn.name,
                    designerName: addOn.designerName,
                    price: addOn.price.salesPriceTotal.rawAmount,
                    quantity: addOn.recommendedQuantity,
                    ean: addOn.ean,
                    familyName: addOn.familyName,
                    itemListId: 'pdp_add_ons',
                    itemListName: 'PDP Add-ons',
                });
        });
        gtmService.addToCart(productsOld, addOns[0].price.salesPrice.currency);
    }

    private getShippingMethodAsString(method?: CheckoutDeliverableType) {
        switch (method) {
        case CheckoutDeliverableType.ClickAndCollect:
            return 'click and collect';
        case CheckoutDeliverableType.CustomerDelivery:
            return 'standard';
        case CheckoutDeliverableType.PickAndPack:
            return 'pick and pack';
        case CheckoutDeliverableType.PickUp:
            return 'pick up';
        default:
            return undefined;
        }
    }

    private getShippingTiers(deliverables: BasketDeliverable[] | CheckOutDeliverable[] | OrderReceiptDeliverable[]) {
        return deliverables.map((d) => this.getShippingMethodAsString(d.type)).join(', ');
    }

    private getProductOrModules(item: any) {
        if (item.modules !== undefined) {
            return item.modules as BaseProductItem[];
        } else {
            return [item] as BaseProductItem[];
        }
    }

    public viewBasket(basket: BasketModel) {
        const items: GA4Item[] = [];
        for (const [outerIndex, deliverable] of basket.deliverables.entries()) {
            const flattenedIndexOffset = basket.deliverables.slice(0, outerIndex).reduce((acc, curr) => acc + curr.items.length, 0);
            (deliverable.items as BaseProductItem[]).forEach((product: BaseProductItem, innerIndex) => {
                const productOrModules = this.getProductOrModules(product);
                productOrModules.forEach((product) => {
                    items.push(
                        gtmService.getGA4Item({
                            product,
                            promoCodes: basket.summary.appliedPromoCodes,
                            isModule: productOrModules.length > 1,
                            index: flattenedIndexOffset + innerIndex,
                            shippingTier: this.getShippingMethodAsString(deliverable.type),
                        }),
                    );
                });
            });
        }

        gtmService.viewCart(
            items,
            {
                currency: basket.summary.salesPriceTotal?.currency,
                value: basket.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(basket.deliverables),
            },
        );
    }

    public removeFromBasket(basket: BasketModel, itemId: number) {
        const items: GA4Item[] = [];
        for (const [outerIndex, deliverable] of basket.deliverables.entries()) {
            const flattenedIndexOffset = basket.deliverables.slice(0, outerIndex).reduce((acc, curr) => acc + curr.items.length, 0);
            (deliverable.items as BaseProductItem[]).forEach((product: BaseProductItem, innerIndex) => {
                if (product.id === itemId) {
                    const productOrModules = this.getProductOrModules(product);
                    productOrModules.forEach((product) => {
                        items.push(
                            gtmService.getGA4Item({
                                product,
                                isModule: productOrModules.length > 1,
                                promoCodes: basket.summary.appliedPromoCodes,
                                index: flattenedIndexOffset + innerIndex,
                                shippingTier: this.getShippingMethodAsString(deliverable.type),
                            }),
                        );
                    });
                }
            });
        }

        const singleProduct = items.length > 0 ? items[0] : undefined;

        if (singleProduct) {
            gtmService.removeFromCart(
                [singleProduct],
                {
                    currency: basket.summary.salesPriceTotal?.currency,
                    value: basket.summary.salesPriceTotal?.rawAmount,
                    shipping_tier: this.getShippingTiers(basket.deliverables),
                },
            );
        }
    }

    public beginCheckout(basket: BasketCheckoutModel) {
        const items: GA4Item[] = [];
        basket.items.forEach((product: BaseProductItem, index) => {
            const productOrModules = this.getProductOrModules(product);
            productOrModules.forEach((product) => {
                items.push(
                    gtmService.getGA4Item({
                        product,
                        isModule: productOrModules.length > 1,
                        promoCodes: basket.summary.appliedPromoCodes,
                        index: index,
                        shippingTier: this.getShippingMethodAsString(basket.deliverables.find((d) => d.itemIds.includes(product.id))?.type),
                    }),
                );
            });
        });

        gtmService.beginCheckout(
            items,
            {
                currency: basket.summary.salesPriceTotal?.currency,
                value: basket.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(basket.deliverables),
            },
        );
    }

    public addShippingInfo(basket: BasketCheckoutModel) {
        const products: BaseProductItem[] = basket.items;
        const items: GA4Item[] = [];
        products.forEach((product: BaseProductItem, index) => {
            const productOrModules = this.getProductOrModules(product);
            productOrModules.forEach((product) => {
                items.push(
                    gtmService.getGA4Item({
                        product,
                        isModule: productOrModules.length > 1,
                        promoCodes: basket.summary.appliedPromoCodes,
                        index: index,
                        shippingTier: this.getShippingMethodAsString(basket.deliverables.find((d) => d.itemIds.includes(product.id))?.type),
                    }),
                );
            });
        });

        gtmService.addShippingInfo(
            items,
            {
                currency: basket.summary.salesPriceTotal?.currency,
                value: basket.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(basket.deliverables),
            },
        );
    }

    public addPaymentInfo(basket: BasketCheckoutModel) {
        const items: GA4Item[] = [];
        basket.items.forEach((product: BaseProductItem, index) => {
            const productOrModules = this.getProductOrModules(product);
            productOrModules.forEach((product) => {
                items.push(
                    gtmService.getGA4Item({
                        product,
                        isModule: productOrModules.length > 1,
                        promoCodes: basket.summary.appliedPromoCodes,
                        index: index,
                        shippingTier: this.getShippingMethodAsString(basket.deliverables.find((d) => d.itemIds.includes(product.id))?.type),
                    }),
                );
            });
        });

        gtmService.addPaymentInfo(
            items,
            {
                currency: basket.summary.salesPriceTotal?.currency,
                value: basket.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(basket.deliverables),
                payment_type: basketStore.selectedPaymentMethod,
                coupon: undefined,
            },
        );
    }

    public purchase(order: OrderReceiptModel, loggedIn: boolean) {
        const items: GA4Item[] = [];
        order.deliverables.forEach((deliverable: OrderReceiptDeliverable, outerIndex) => {
            const flattenedIndexOffset = order.deliverables.slice(0, outerIndex).reduce((acc, curr) => acc + curr.items.length, 0);
            deliverable.items.forEach((product: OrderReceiptBaseItemModel, innerIndex) => {
                const productOrModules = this.getProductOrModules(product);
                productOrModules.forEach((product) => {
                    items.push(
                        gtmService.getGA4Item({
                            product,
                            isModule: productOrModules.length > 1,
                            promoCodes: order.summary.appliedPromoCodes,
                            index: flattenedIndexOffset + innerIndex,
                            shippingTier: this.getOrderReceiptDeliverableTypeAsString(deliverable.type),
                        }),
                    );
                });
            });
        });

        const hasUsedGiftCard = order.summary.giftCards && order.summary.giftCards?.length > 0;
        const payments = order.summary.payments.map(p => p.terminalName);
        if (hasUsedGiftCard) {
            payments.push('giftcard');
        }

        gtmService.purchase(
            items,
            {
                currency: order.summary.salesPriceTotal?.currency,
                value: order.summary.salesPriceTotal?.rawAmount,
                shipping_tier: this.getShippingTiers(order.deliverables),
                payment_type: payments.join(', '),
                coupon: undefined,
                shipping: order.summary.freightPriceTotal?.rawAmount,
                tax: order.summary.salesTax.rawAmount,
                transaction_id: order.orderNumber,
            },
            {
                userId: order.invoiceAddress?.buyerContactInformation?.emailAddress,
                customerType: order.invoiceAddress?.buyer?.vatNumber ? 'business' : 'private',
                country: order.invoiceAddress?.address?.country,
                firstname: order.invoiceAddress?.buyer?.firstName,
                lastname: order.invoiceAddress?.buyer?.lastName,
                loginStatus: loggedIn ? 'logged in' : 'not logged in',
                phoneNumber: order.invoiceAddress?.buyerContactInformation?.phoneNumber,
                postCode: order.invoiceAddress?.address?.postalCode,
                street: order.invoiceAddress?.address?.street,
            },
        );
    }

    public purchaseOld(order: OrderReceiptModel) {
        const products: {
            name: string
            id: string
            price: number
            brand: string
            dimension8: string
            dimension14: string
            quantity: number
        }[] = [];
        order.deliverables.forEach((deliverable: OrderReceiptDeliverable) => {
            deliverable.items.forEach((product: OrderReceiptBaseItemModel) => {
                const productOrModules = this.getProductOrModules(product);
                productOrModules.forEach((product) => {
                    products.push(
                        {
                            name: product.name,
                            id: product.sku,
                            price: product.price?.salesPriceTotal?.rawAmount ?? 0,
                            brand: product.metaData?.brand ?? '',
                            dimension8: product.metaData?.ean ?? '',
                            dimension14: product.metaData?.family ?? '',
                            quantity: product.quantity,
                        },
                    );
                });
            });
        });

        gtmService.purchaseOld(
            products,
            order.summary.salesPriceTotal?.currency ?? '',
            {
                id: order.orderNumber,
                coupon: order.summary.appliedPromoCodes.map((promo) => promo.redeemCode).join(', '),
                shipping: order.summary.freightPriceTotal?.rawAmount ?? 0,
                tax: order.summary.salesTax.rawAmount,
                revenue: order.summary.salesPriceTotal?.rawAmount ?? 0,
            });
    }
}

export default new BasketTrackingService();
